import { ImagesSkusFiltersBlackSvg } from '@/assets/images_skus.assets';
import Image from 'next/image';
import { useEffect, useRef } from 'react';
import SkusMobileAlgoliaShowMenuFilter from './SkusMobileAlgoliaShowMenuFilter';
import SkusMobileAlgoliaShowStatusFilter from './SkusMobileAlgoliaShowStatusFilter';
import SkusMobileAlgoliaShowPricesFilter from './SkusMobileAlgoliaShowPricesFilter';
import SkusMobileAlgoliaRefinementList from './SkusMobileAlgoliaRefinementList';
import { useCurrentRefinements } from 'react-instantsearch';
import DefaultButton from '@/components/common/default/DefaultButton';
import { ImagesSkusCloseSvg } from '@/assets/images_skus.assets';
import { CgClose } from 'react-icons/cg';
import { SkusMobileAlgoliaShowFiltersProps } from '@/types/props/components/skus';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { SkusEnum } from '@/types/translations/SkusEnum';

export default function SkusMobileAlgoliaShowFilters({
  setFiltersAreOpen,
  filtersAreOpen,
}: SkusMobileAlgoliaShowFiltersProps) {
  const { translate } = useTypedTranslation<SkusEnum>('skus');
  const { items, refine } = useCurrentRefinements();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const dialogTitleId = 'filter-dialog-title';

  // Handle closing the dialog when the user clicks outside
  const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    const dialogDimensions = dialogRef.current?.getBoundingClientRect();
    if (
      dialogDimensions &&
      (e.clientX < dialogDimensions.left ||
        e.clientX > dialogDimensions.right ||
        e.clientY < dialogDimensions.top ||
        e.clientY > dialogDimensions.bottom)
    ) {
      handleClose();
    }
  };

  // Handle ESC key and close button
  const handleClose = () => {
    document.body.style.overflow = '';
    setFiltersAreOpen(false);
  };

  useEffect(() => {
    if (filtersAreOpen) {
      dialogRef.current?.showModal();
      document.body.style.overflow = 'hidden';
    } else {
      dialogRef.current?.close();
      document.body.style.overflow = '';
    }
  }, [filtersAreOpen]);

  return (
    <dialog
      ref={dialogRef}
      className="tw-backdrop:bg-black tw-backdrop:bg-opacity-50 tw-fixed tw-inset-0 tw-z-[9999] tw-m-0 tw-h-full tw-max-h-screen tw-min-h-screen tw-w-full tw-max-w-none tw-overflow-y-scroll tw-bg-white tw-p-0"
      aria-labelledby={dialogTitleId}
      aria-modal="true"
      onClick={handleBackdropClick}
    >
      <div className="tw-flex tw-h-full tw-flex-col">
        <header className="tw-flex tw-flex-col tw-gap-3 tw-border-0.75 tw-border-l-0 tw-border-r-0 tw-border-t-0 tw-border-solid tw-border-custom-gray tw-px-8 tw-pb-3 tw-pt-12">
          <div className="tw-flex tw-items-center tw-justify-between">
            <div className="tw-flex tw-w-full tw-items-center tw-gap-3">
              <Image
                src={ImagesSkusFiltersBlackSvg}
                width={24}
                height={14.4}
                alt=""
                aria-hidden="true"
              />
              <h2
                id={dialogTitleId}
                className="tw-m-0 tw-font-plusJakartaSans tw-text-xl tw-font-semibold tw-text-textBlack"
              >
                {translate(SkusEnum['filters'])}
              </h2>
            </div>
            <DefaultButton
              ref={closeButtonRef}
              onClick={handleClose}
              aria-label={translate(SkusEnum['close_filters'])}
              className="tw-rounded-full tw-p-2 hover:tw-bg-gray-100 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-offset-2"
            >
              <CgClose aria-hidden="true" />
            </DefaultButton>
          </div>
          {items.length > 0 && (
            <div
              className="tw-flex tw-flex-wrap tw-items-center tw-justify-start tw-gap-2 tw-pt-2"
              role="region"
              aria-label={translate(SkusEnum['active_filters']) || 'Active filters'}
            >
              {items.map(filter =>
                filter.refinements.map(refinement => (
                  <DefaultButton
                    key={refinement.value}
                    className="tw-flex tw-items-center tw-gap-4 tw-rounded-lg tw-bg-customGray tw-px-4 tw-py-2 tw-transition-colors hover:tw-bg-gray-200"
                    onClick={() => refine(refinement)}
                  >
                    <span>
                      {filter.label === 'hasBid'
                        ? translate(SkusEnum['has_bids'])
                        : filter.label === 'hasAsk'
                          ? translate(SkusEnum['has_asks'])
                          : refinement.label}
                    </span>{' '}
                    <Image
                      src={ImagesSkusCloseSvg}
                      width={10.5}
                      height={10.5}
                      alt=""
                      aria-hidden="true"
                    />
                    <span className="tw-sr-only">
                      {translate(SkusEnum['remove_filterName_filter']).replace(
                        '{filterName}',
                        String(refinement.value)
                      )}
                    </span>
                  </DefaultButton>
                ))
              )}
            </div>
          )}
        </header>

        <div className="tw-flex-1 tw-overflow-y-auto tw-px-8">
          <section className="tw-pt-3">
            <nav aria-label={translate(SkusEnum['primary_filters']) || 'Primary filters'}>
              <ul className="tw-flex tw-w-full tw-list-none tw-flex-col tw-gap-4 tw-border tw-border-l-0 tw-border-r-0 tw-border-t-0 tw-border-solid tw-border-custom-gray tw-p-0 tw-pb-4">
                <SkusMobileAlgoliaShowMenuFilter />
                <SkusMobileAlgoliaShowStatusFilter />
                <SkusMobileAlgoliaShowPricesFilter />
              </ul>
            </nav>
            <nav aria-label={translate(SkusEnum['secondary_filters']) || 'Secondary filters'}>
              <ul className="tw-flex tw-w-full tw-list-none tw-flex-col tw-gap-4 tw-border tw-border-l-0 tw-border-r-0 tw-border-t-0 tw-border-solid tw-border-custom-gray tw-p-0 tw-pb-4">
                <SkusMobileAlgoliaRefinementList
                  attributeName="brand.countryOfOrigin"
                  title={translate(SkusEnum['country'])}
                />
                <SkusMobileAlgoliaRefinementList
                  attributeName="brand.name"
                  title={translate(SkusEnum['brand'])}
                />
                <SkusMobileAlgoliaRefinementList
                  attributeName="brand.region"
                  title={translate(SkusEnum['region'])}
                />
                <SkusMobileAlgoliaRefinementList
                  attributeName="bottleSizeInMl"
                  title={translate(SkusEnum['bottle_size'])}
                />
                <SkusMobileAlgoliaRefinementList
                  attributeName="subCategory"
                  title={translate(SkusEnum['subcategory'])}
                />
                <SkusMobileAlgoliaRefinementList
                  attributeName="volumeInPercent"
                  title={translate(SkusEnum['alcoholPercentage'])}
                />
                <SkusMobileAlgoliaRefinementList
                  attributeName="productAge"
                  title={translate(SkusEnum['age'])}
                />
                <SkusMobileAlgoliaRefinementList
                  attributeName="yearBottled"
                  title={translate(SkusEnum['bottled_year'])}
                />
              </ul>
            </nav>
          </section>
        </div>
      </div>
    </dialog>
  );
}
